import React from 'react'
import { useHistory, Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  Paper,
  Button,
  Typography,
  Tooltip,
  CircularProgress,
} from '@material-ui/core'
import EventNoteIcon from '@material-ui/icons/EventNote'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import BarChartIcon from '@material-ui/icons/BarChart'
import SettingsIcon from '@material-ui/icons/Settings'
import EventIcon from '@material-ui/icons/Event'
import DateRangeIcon from '@material-ui/icons/DateRange'
import OpacityIcon from '@material-ui/icons/Opacity'
import AssignmentIcon from '@material-ui/icons/Assignment'
import StorageIcon from '@material-ui/icons/Storage'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart'
import AssessmentIcon from '@material-ui/icons/Assessment'
import TimelineIcon from '@material-ui/icons/Timeline'
import ShowChartIcon from '@material-ui/icons/ShowChart'
import BuildIcon from '@material-ui/icons/Build'
import MapIcon from '@material-ui/icons/Map'
import TerrainIcon from '@material-ui/icons/Terrain'
import BusinessIcon from '@material-ui/icons/Business'
import LocalDrinkIcon from '@material-ui/icons/LocalDrink'
import useParams from 'hooks/useParams'
import { Meadow, Management, CattleSumm, Farm } from 'utils/api/models'
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  parseJSON,
  toDate,
  isSameDay,
  isBefore,
  isAfter,
} from 'date-fns'
import esLocale from 'date-fns/locale/es'

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    gap: '20px',
    alignItems: 'stretch',
    overflow: 'hidden',
  },
  mainArea: {
    flex: '0 0 75%',
    minWidth: 0,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    gap: '16px',
  },
  sideArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: 0,
    minHeight: 0,
  },
  card: {
    borderRadius: '10px',
    overflow: 'hidden',
    minHeight: 0,
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  cardHeaderIcon: {
    color: '#fff',
    fontSize: '1.3rem',
  },
  cardHeaderTitle: {
    fontWeight: 'bold',
    fontSize: '1.05rem',
    color: '#fff',
    letterSpacing: '0.02em',
  },
  cardBody: {
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  navBtn: {
    justifyContent: 'space-between',
    padding: '11px 16px',
    fontSize: '0.92rem',
    fontWeight: 600,
    borderRadius: '7px',
    textTransform: 'none',
    borderColor: '#c8e6a0',
    color: '#333',
    backgroundColor: '#f5fbee',
    '&:hover': {
      backgroundColor: '#e2f5c8',
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-disabled': {
      backgroundColor: '#f5f5f5',
      borderColor: '#e0e0e0',
      color: '#bdbdbd',
    },
  },
  navBtnIcon: {
    color: theme.palette.primary.main,
    fontSize: '1.1rem !important',
  },
  // Widget semana
  widgetBox: {
    borderRadius: '10px',
    overflow: 'hidden',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  widgetHeader: {
    backgroundColor: theme.palette.primary.main,
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  widgetHeaderText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  widgetBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '10px',
    backgroundColor: '#fff',
  },
  dayLabel: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '4px',
    marginTop: '8px',
    '&:first-child': { marginTop: 0 },
  },
  taskRow: {
    padding: '4px 8px',
    background: '#f5fbee',
    borderRadius: '4px',
    marginBottom: '3px',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  taskText: {
    fontSize: '0.78rem',
    color: '#305600',
    lineHeight: 1.3,
  },
  emptyMsg: {
    color: '#bbb',
    fontSize: '0.82rem',
    textAlign: 'center',
    marginTop: '20px',
  },
  // Calendario de leche
  calBox: {
    borderRadius: '10px',
    overflow: 'hidden',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  calHeader: {
    backgroundColor: theme.palette.primary.main,
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  calHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  calMonthLabel: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.88rem',
    textTransform: 'capitalize',
  },
  calTodayLabel: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: '0.7rem',
  },
  calDelayBadge: {
    backgroundColor: '#d32f2f',
    color: '#fff',
    borderRadius: '12px',
    padding: '2px 10px',
    fontSize: '0.72rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  calBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 10px 6px',
    backgroundColor: '#fff',
  },
  calGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '3px',
  },
  calDayName: {
    textAlign: 'center',
    fontSize: '0.62rem',
    fontWeight: 700,
    color: '#aaa',
    paddingBottom: '4px',
    textTransform: 'uppercase',
  },
  calCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '3px 0',
  },
  calDayNum: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.72rem',
    fontWeight: 500,
    color: '#555',
  },
  calDayToday: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 700,
  },
  calDayOtherMonth: {
    color: '#ddd',
  },
  calDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    marginTop: '2px',
  },
  calDotOk: { backgroundColor: theme.palette.primary.main },
  calDotDelay: { backgroundColor: '#d32f2f' },
}))

// ── Widget: Tareas de la semana ──────────────────────────────────────────────

function WeeklyTasksWidget() {
  const classes = useStyles()
  const { farmId } = useParams()
  const [tasksByDay, setTasksByDay] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const meadows = await Meadow.all()
        const meadowIds = meadows.filter((m) => !m.is_template).map((m) => m.id)
        if (!meadowIds.length) { setLoading(false); return }

        const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
        const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 })

        const managements = await Management.allInMeadows(meadowIds, weekStart, weekEnd)

        // Aplanar todas las tareas
        const allTasks = managements.flatMap((m) =>
          (m.task_groups || []).flatMap((tg) =>
            (tg.tasks || []).map((t) => ({
              id: t.id,
              name: t.name,
              machinery: t.machinery,
              suggested_date: t.suggested_date,
            })),
          ),
        )

        // Agrupar por día de la semana
        const days = eachDayOfInterval({ start: weekStart, end: weekEnd })
        const grouped = days
          .map((day) => {
            const dayKey = format(day, 'yyyy-MM-dd')
            const tasks = allTasks.filter(
              (t) =>
                t.suggested_date &&
                format(toDate(parseJSON(t.suggested_date)), 'yyyy-MM-dd') === dayKey,
            )
            return { day, tasks }
          })
          .filter((d) => d.tasks.length > 0)

        if (!cancelled) setTasksByDay(grouped)
      } catch (e) {
        // Sin datos disponibles
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [farmId])

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
  const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 })
  const headerLabel = `${format(weekStart, 'd', { locale: esLocale })} — ${format(weekEnd, 'd MMM', { locale: esLocale })}`

  return (
    <Paper className={classes.widgetBox} elevation={2}>
      <div className={classes.widgetHeader}>
        <EventIcon style={{ color: '#fff', fontSize: '1.1rem' }} />
        <Typography className={classes.widgetHeaderText}>
          Semana {headerLabel}
        </Typography>
      </div>
      <div className={classes.widgetBody}>
        {loading ? (
          <Box display='flex' justifyContent='center' mt={2}>
            <CircularProgress size={22} style={{ color: '#5faa00' }} />
          </Box>
        ) : tasksByDay.length === 0 ? (
          <Typography className={classes.emptyMsg}>
            Sin tareas esta semana
          </Typography>
        ) : (
          tasksByDay.map(({ day, tasks }) => (
            <React.Fragment key={day.toISOString()}>
              <Typography className={classes.dayLabel}>
                {format(day, 'EEEE d', { locale: esLocale })}
              </Typography>
              {tasks.map((task) => (
                <div key={task.id} className={classes.taskRow}>
                  <Typography className={classes.taskText}>
                    {task.name}
                    {task.machinery?.name ? ` · ${task.machinery.name}` : ''}
                  </Typography>
                </div>
              ))}
            </React.Fragment>
          ))
        )}
      </div>
    </Paper>
  )
}

// ── Widget: Calendario mensual de leche ─────────────────────────────────────

const CAL_DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

function MilkCalendarWidget() {
  const classes = useStyles()
  const { farmId } = useParams()
  const [registeredDates, setRegisteredDates] = React.useState(new Set())
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const raw = await CattleSumm.all()
        const records = Array.isArray(raw) ? raw : (raw?.data || [])
        const dates = new Set(records.map((r) => r.date.slice(0, 10)))
        if (!cancelled) setRegisteredDates(dates)
      } catch (e) {
        // sin datos
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [farmId])

  const today = new Date()
  const monthStart = startOfMonth(today)
  const monthEnd = endOfMonth(today)
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 })
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
  const allDays = eachDayOfInterval({ start: calStart, end: calEnd })

  // Días pasados del mes sin registro = atraso (excluye hoy — la ausencia de hoy aún no es atraso)
  const pastDays = eachDayOfInterval({ start: monthStart, end: today }).filter(
    (d) => !isSameDay(d, today),
  )
  const delayCount = pastDays.filter(
    (d) => !registeredDates.has(format(d, 'yyyy-MM-dd')),
  ).length

  const monthLabel = format(today, 'MMMM yyyy', { locale: esLocale })
  const todayLabel = format(today, "d 'de' MMMM", { locale: esLocale }) // eslint-disable-line quotes

  return (
    <Paper className={classes.calBox} elevation={2}>
      <div className={classes.calHeader}>
        <div className={classes.calHeaderLeft}>
          <Typography className={classes.calMonthLabel}>{monthLabel}</Typography>
          <Typography className={classes.calTodayLabel}>Hoy: {todayLabel}</Typography>
        </div>
        {!loading && delayCount > 0 && (
          <span className={classes.calDelayBadge}>
            {delayCount} {delayCount === 1 ? 'día sin registro' : 'días sin registro'}
          </span>
        )}
        {!loading && delayCount === 0 && (
          <span className={classes.calDelayBadge} style={{ backgroundColor: '#388e3c' }}>
            Al día ✓
          </span>
        )}
      </div>
      <div className={classes.calBody}>
        {loading ? (
          <Box display='flex' justifyContent='center' mt={2}>
            <CircularProgress size={20} style={{ color: '#5faa00' }} />
          </Box>
        ) : (
          <div className={classes.calGrid}>
            {CAL_DAY_NAMES.map((n) => (
              <div key={n} className={classes.calDayName}>{n}</div>
            ))}
            {allDays.map((day) => {
              const key = format(day, 'yyyy-MM-dd')
              const inMonth = !isBefore(day, monthStart) && !isAfter(day, monthEnd)
              const isToday = isSameDay(day, today)
              const isPast = isBefore(day, today) && inMonth
              const registered = registeredDates.has(key)
              return (
                <div key={key} className={classes.calCell}>
                  <div
                    className={[
                      classes.calDayNum,
                      isToday ? classes.calDayToday : '',
                      !inMonth ? classes.calDayOtherMonth : '',
                    ].join(' ')}
                  >
                    {format(day, 'd')}
                  </div>
                  {inMonth && isPast && (
                    <div
                      className={[
                        classes.calDot,
                        registered ? classes.calDotOk : classes.calDotDelay,
                      ].join(' ')}
                    />
                  )}
                  {inMonth && isToday && registered && (
                    <div className={[classes.calDot, classes.calDotOk].join(' ')} />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Paper>
  )
}

// ── Sección card de navegación ───────────────────────────────────────────────

function SectionCard({ icon, title, items }) {
  const classes = useStyles()
  const history = useHistory()

  return (
    <Paper className={classes.card} elevation={2}>
      <div className={classes.cardHeader}>
        <span className={classes.cardHeaderIcon}>{icon}</span>
        <Typography className={classes.cardHeaderTitle}>{title}</Typography>
      </div>
      <div className={classes.cardBody}>
        {items.map(({ label, route, disabled, disabledReason, icon }) => {
          const btn = (
            <Button
              key={label}
              fullWidth
              variant='outlined'
              className={classes.navBtn}
              disabled={disabled}
              endIcon={icon ? <span className={classes.navBtnIcon}>{icon}</span> : null}
              onClick={() => !disabled && history.push(route)}
            >
              {label}
            </Button>
          )
          return disabled && disabledReason ? (
            <Tooltip key={label} title={disabledReason} placement='right'>
              <span>{btn}</span>
            </Tooltip>
          ) : (
            <React.Fragment key={label}>{btn}</React.Fragment>
          )
        })}
      </div>
    </Paper>
  )
}

// ── Home principal ───────────────────────────────────────────────────────────

function Home() {
  const classes = useStyles()
  const { farmOwnerId, farmId, year } = useParams()
  const businesses = useSelector((state) => state.businesses)
  const routeBusinessId = useSelector((state) => state.routeParams.businessId)
  const user = useSelector((state) => state.user)

  const isAdmin = user?.is_admin || user?.is_super_admin

  // Mide la posición real del wrapper para calcular la altura disponible exacta
  const wrapperRef = React.useRef(null)
  const [wrapperHeight, setWrapperHeight] = React.useState(0)

  React.useEffect(() => {
    function measure() {
      if (!wrapperRef.current) return
      const top = wrapperRef.current.getBoundingClientRect().top
      // 56px = paddingBottom del containerContent (espacio para la barra inferior)
      setWrapperHeight(window.innerHeight - top - 56)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const businessId =
    businesses.find((b) => b.id === routeBusinessId)?.id || businesses[0]?.id
  const hasBusinessId = !!businessId
  const noBusinessMsg = 'Agrega una empresa'

  const sections = [
    {
      id: 'planificacion',
      icon: <EventNoteIcon style={{ color: '#fff' }} />,
      title: 'Planificación',
      items: [
        {
          label: 'Calendario manejos',
          route: `/${farmOwnerId}/${farmId}/planificacion/calendario-manejos/${year}`,
          icon: <DateRangeIcon />,
        },
        {
          label: 'Registro datos lecheros',
          route: `/${farmOwnerId}/${farmId}/planificacion/visitas-prediales/${year}`,
          icon: <OpacityIcon />,
        },
        {
          label: 'Plantillas manejos',
          route: `/${farmOwnerId}/${farmId}/planificacion/plantillas-manejos`,
          icon: <AssignmentIcon />,
        },
        {
          label: 'Base de datos',
          route: '/planificacion/base-de-datos',
          icon: <StorageIcon />,
        },
      ],
    },
    {
      id: 'finanzas',
      icon: <AttachMoneyIcon style={{ color: '#fff' }} />,
      title: 'Finanzas',
      items: [
        {
          label: 'Ventas',
          route: `/${farmOwnerId}/${businessId}/finanzas/ventas/${year}`,
          disabled: !hasBusinessId,
          disabledReason: noBusinessMsg,
          icon: <TrendingUpIcon />,
        },
        {
          label: 'Compras',
          route: `/${farmOwnerId}/${businessId}/finanzas/compras/${year}`,
          disabled: !hasBusinessId,
          disabledReason: noBusinessMsg,
          icon: <ShoppingCartIcon />,
        },
        {
          label: 'Estado de resultado',
          route: `/${farmOwnerId}/${businessId}/finanzas/estado-resultado/${year}`,
          disabled: !hasBusinessId,
          disabledReason: noBusinessMsg,
          icon: <AssessmentIcon />,
        },
        {
          label: 'Estado de resultado mensual',
          route: `/${farmOwnerId}/${businessId}/finanzas/estado-resultado-mensual/${year}`,
          disabled: !hasBusinessId,
          disabledReason: noBusinessMsg,
          icon: <TimelineIcon />,
        },
      ],
    },
    {
      id: 'analitica',
      icon: <BarChartIcon style={{ color: '#fff' }} />,
      title: 'Analítica',
      items: [
        {
          label: 'Leche por día',
          route: `/${farmOwnerId}/${farmId}/analitica/leche/dia`,
          icon: <ShowChartIcon />,
        },
        {
          label: 'Leche por mes',
          route: `/${farmOwnerId}/${farmId}/analitica/leche/mes`,
          icon: <BarChartIcon />,
        },
        {
          label: 'Insumos y maquinaria',
          route: `/${farmOwnerId}/${farmId}/analitica`,
          icon: <BuildIcon />,
        },
      ],
    },
    ...(isAdmin
      ? [
          {
            id: 'administracion',
            icon: <SettingsIcon style={{ color: '#fff' }} />,
            title: 'Administración',
            items: [
              {
                label: 'Mapas',
                route: `/${farmOwnerId}/${farmId}/mapas/datos-geograficos`,
                icon: <MapIcon />,
              },
              {
                label: 'Campos',
                route: `/${farmOwnerId}/administracion-campos`,
                icon: <TerrainIcon />,
              },
              {
                label: 'Empresas',
                route: `/${farmOwnerId}/administracion-empresas`,
                icon: <BusinessIcon />,
              },
              {
                label: 'Lecherías',
                route: `/${farmOwnerId}/unidades-productivas`,
                icon: <LocalDrinkIcon />,
              },
            ],
          },
        ]
      : []),
  ]

  return (
    <Box
      ref={wrapperRef}
      className={classes.wrapper}
      style={wrapperHeight ? { height: wrapperHeight } : {}}
    >
      <Box className={classes.mainArea}>
        {sections.map((s) => (
          <SectionCard
            key={s.id}
            icon={s.icon}
            title={s.title}
            items={s.items}
          />
        ))}
      </Box>

      <Box className={classes.sideArea}>
        <WeeklyTasksWidget />
        <MilkCalendarWidget />
      </Box>
    </Box>
  )
}

// ── Redirect desde "/" ───────────────────────────────────────────────────────

export function HomeIndexRedirect() {
  const farmOwners = useSelector(({ farmOwners }) => farmOwners)
  const farms = useSelector(({ farms }) => farms)
  const routeParams = useSelector(({ routeParams }) => routeParams)
  const [noFarms, setNoFarms] = React.useState(false)

  const farmOwnerId = Number.isInteger(routeParams.farmOwnerId)
    ? routeParams.farmOwnerId
    : farmOwners[0]?.id

  const farmId = Number.isInteger(routeParams.farmId)
    ? routeParams.farmId
    : farms[0]?.id

  // Si el propietario no tiene ningún campo creado, el resto de los sagas
  // nunca logra fijar un farmId y esta página se queda esperando para
  // siempre. Se consulta directo para saberlo con certeza (en vez de
  // esperar una señal global de carga) y se manda igual al Home, pero
  // en la ruta sin campo — queda como para cualquier usuario, sin
  // seleccionar ningún campo.
  React.useEffect(() => {
    if (!farmOwnerId || farmId) return
    let cancelled = false
    Farm.all().then((allFarms) => {
      if (!cancelled && allFarms.length === 0) setNoFarms(true)
    })
    return () => {
      cancelled = true
    }
  }, [farmOwnerId, farmId])

  if (farmOwnerId && farmId) {
    return <Redirect to={`/${farmOwnerId}/${farmId}`} />
  }

  if (noFarms && farmOwnerId) {
    return <Redirect to={`/${farmOwnerId}`} />
  }

  return null
}

export default Home
